import { Sidebar } from "src/components/Sidebar/Sidebar"
import { ProjectImage } from "src/components/ProjectImage/ProjectImage"
import "./styles.scss"

const MainContent = () => {
  return (
    <div className="main-content p-4">
      <div className="header mb-4">
        <h1 className="title">Pacer BIDR Integration</h1>
        <h2 className="subtitle">Full Stack Development of BIDR Integration</h2>
      </div>

      <div className="description mb-5">
        <p>
          BIDR is a vehicle auction platform that allows its users to post cars for online auctions. We partnered with BIDR to offer our checkout and shipping tools through the platform. As a part of this integration, I modernized Pacer's existing checkout flow, updated styling, and added new features specific to BIDR users.
        </p>
      </div>

      <div className="project-screenshot">
        <ProjectImage 
          src="/assets/Screenshots/bidrHome.png" 
          alt="BIDR Integration Homepage"
          description="BIDR platform with Pacer integration"
        />
      </div>

      <div className="description mb-5">
        <p>
          The integration was built using TypeScript, React, and Node.js (Express). This project required close collaboration with the BIDR team to ensure a seamless experience for their users while maintaining the functionality and reliability of Pacer's tools.
        </p>
      </div>

      <div className="description things-i-worked-on mb-5">
        <h4>Things I Worked On</h4>
        <p>While working on the BIDR integration, I developed several key features:</p>
        <ul>
          <li><a href="#checkout-flow" className="work-link">Modernized Checkout Flow</a></li>
          <li><a href="#styling" className="work-link">UI/UX Improvements</a></li>
          <li><a href="#shipping" className="work-link">Automated Document Generation</a></li>
        </ul>
      </div>

      <div id="checkout-flow" className="description mb-5">
        <h4>Modernized Checkout Flow</h4>
        <p>
          One of the main components of this project was updating Pacer's checkout flow to work seamlessly with BIDR's auction platform. This involved:
        </p>
        <ul>
          <li>Redesigning the checkout process for better user experience</li>
          <li>Adding features such as escrow payment protection</li>
          <li>Creating a responsive design that works on all devices</li>
          <li>Extensive testing and debugging of existing infrastructure</li>
        </ul>
        <p>
          Much of the work on this project involved testing the existing checkout infrastructure that was created several years ago (prior to my time at Pacer). This was a challenging task, as much of the code neededed to be re-written and mended. This was a great opportunity to learn about advanced state management in React, using hooks, and production level typing. 
        </p>
        <ProjectImage 
          src="/assets/Screenshots/bidrFeatures.png" 
          alt="BIDR Checkout Flow"
          description="Feature selection for BIDR chieckout flow"
          width="90%"
        />bidr
      </div>

      <div id="styling" className="description mb-5">
        <h4>UI/UX Improvements</h4>
        <p>
          A significant part of the project involved updating the styling to match BIDR's brand while maintaining Pacer's functionality:
        </p>
        <ProjectImage 
          src="/assets/Screenshots/bidrFeeSummary.png" 
          alt="BIDR Styling"
          description="Updated UI with BIDR branding"
          width="90%"
        />
        <p>
          The styling updates included:
        </p>
        <ul>
          <li>Implementing a new color scheme and typography</li>
          <li>Creating responsive layouts for all screen sizes</li>
          <li>Updating Email styling and content</li>
        </ul>
        <ProjectImage 
          src="/assets/Screenshots/bidrEmailRedesign.png" 
          alt="BIDR Styling"
          description="Updated Email styling and content"
          width="90%"
        />
      </div>

      <div id="shipping" className="description mb-5">
        <h4>Document Generation</h4>
        <p>
          As a part of our offerings to BIDR users, we wanted to automatically generate documents to streamline the vehicle sales process. I used templates and PDF generation libraries to create a digital Bill of Sale and Odometer Disclosure. In the future, we want to add digital registration documents, and plan to use AI to generate accurate information regardless of state.
        </p>
        <ProjectImage 
          src="/assets/Screenshots/bidrBOS.png" 
          alt="BIDR Shipping Tools"
          description="generated Bill of Sale"
          width="90%"
        />
      </div>

      <div id="conclusion" className="description mb-5">
        <h4>Conclusion</h4>
        <p>
          The BIDR integration project was a successful partnership that expanded Pacer's reach while enhancing BIDR's platform capabilities. By modernizing our checkout flow, implementing robust API integrations, updating the UI/UX, and providing powerful document generation tools, we created a seamless experience for users across both platforms.
        </p>
        <p>
          This project demonstrated the value of thoughtful integration design and close collaboration between development teams. The resulting system has processed thousands of vehicle transactions and continues to be a key component of both Pacer's and BIDR's offerings.
        </p>
      </div>
    </div>
  )
}

interface ProjectLayoutProps {
  sidebar: React.ReactNode;
  children: React.ReactNode;
}

const ProjectLayout: React.FC<ProjectLayoutProps> = ({ sidebar, children }) => (
  <div className="container-fluid p-0">
    <div className="row g-0">
      <div className="col-md-3">{sidebar}</div>
      <div className="col-md-9">{children}</div>
    </div>
  </div>
)

export default function PacerBidrProject() {
  const sidebarProps = {
    toolUrl: "https://pacer.bidr.co",
    responsibilities: [
      "TypeScript React",
      "Node.js",
      "Express",
      "Shipping API Integration",
      "Stripe API Integration",
      "UI/UX Design",
      "Responsive Design"
    ]
  }

  return (
    <ProjectLayout sidebar={<Sidebar {...sidebarProps} />}>
      <MainContent />
    </ProjectLayout>
  )
}
